/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    Database, FileText, Loader2
} from 'lucide-react';
import {
    redisCollectionService
} from 'shared/api/services/BEX/redisCollection.service';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from 'shared/components/ui/dialog';
import DSResourceList from './DSResourceList';
import { DSButton } from '../ui';

/**
 * DSCollectionsList — DS-themed list for Library → Collections.
 * Per user feedback: knowledge collections live INSIDE Library (no
 * "Open Knowledge Builder" link-out anymore). Click → navigates to the
 * Knowledge Builder collection edit page, but the entry point is here.
 */
export default function DSCollectionsList() {
    const navigate = useNavigate();
    const {
        oragID, subOragID
    } = useParams();
    const { actions, hasMilvusCollection, collections } = useContext(SubOrgContext);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', vectorStoreType: 'MongoDB' });
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    function reload() {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        redisCollectionService.listCollections(oragID, subOragID)
            .then((res) => {
                const arr = res?.data || res || [];
                setItems(Array.isArray(arr) ? arr : []);
            })
            .catch((err) => setError(err?.message || 'Failed to load'))
            .finally(() => setLoading(false));
    }
    useEffect(reload, [oragID, subOragID]);

    function openItem(c) {
        navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/knowledge/${c._id}/${encodeURIComponent(c.name || '')}`);
    }

    async function handleCreate() {
        const name = createForm.name.trim();
        if (name.length < 3) { setCreateError('Name must be at least 3 characters.'); return; }
        setCreating(true);
        setCreateError('');
        try {
            const res = await redisCollectionService.addCollection({
                organizationId: oragID,
                subOrganizationId: subOragID,
                name,
                vectorStoreType: createForm.vectorStoreType,
                dimensions: 1024,
                chunkingStrategy: 'by page',
                embeddingModel: 'jina',
                pdfPageImages: false,
            });
            const created = res?.data || res;
            setShowCreate(false);
            setCreateForm({ name: '', vectorStoreType: 'MongoDB' });
            if (created?._id) {
                actions({ type: 'SET_COLLECTIONS', payload: [...(collections || []), created] });
                navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/knowledge/${created._id}/${encodeURIComponent(created.name || name)}`);
            } else {
                reload();
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) setCreateError('A collection with this name already exists.');
            else if (status === 403) setCreateError('Plan limit reached — cannot create more collections.');
            else setCreateError(err?.response?.data?.message || err?.message || 'Failed to create collection.');
        } finally {
            setCreating(false);
        }
    }

    async function handleDelete(c) {
        try {
            await redisCollectionService.deleteCollection({
                organizationId: oragID,
                subOrganizationId: subOragID,
                collectionId: c._id,
            });
            setConfirmDelete(null);
            reload();
        } catch (err) {
            alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
        }
    }

    return (
        <>
        <DSResourceList
            items={items}
            loading={loading}
            error={error}
            getKey={(it) => it._id}
            getCard={(it) => ({
                icon: Database,
                tone: 'violet',
                title: it.name || it._id,
                subtitle: 'COLLECTION',
                description: it.description || '',
                version: it.version ? `v${it.version}` : null,
                stats: [
                    typeof it.fileCount === 'number' && {
                        icon: FileText,
                        label: it.fileCount === 1 ? 'file' : 'files',
                        value: it.fileCount
                    }
                ].filter(Boolean),
                idLine: it._id,
                createdBy: it.createdBy || null,
                createdAt: it.created || null,
                searchTerms: [it.name, it.description]
            })}
            onPick={openItem}
            onEdit={openItem}
            onDelete={(c) => setConfirmDelete(c)}
            onCreate={() => { setCreateForm({ name: '', vectorStoreType: 'MongoDB' }); setCreateError(''); setShowCreate(true); }}
            newLabel="New collection"
            searchPlaceholder="Search collections…"
            emptyTitle="No collections yet"
            emptyDescription="Index your first document set — your assistants ground their answers in here."
            emptyIcon={Database}
        />

        <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Delete this collection?</AlertDialogTitle>
                    <AlertDialogDescription>
                        <b>{confirmDelete?.name || confirmDelete?._id}</b> and all its indexed files will be permanently removed.
                        Any assistant using this collection will lose its grounding context.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={() => handleDelete(confirmDelete)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        Delete
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>

        <Dialog open={showCreate} onOpenChange={(o) => { if (!creating) setShowCreate(o); }}>
            <DialogContent className="sm:max-w-[420px]">
                <DialogHeader>
                    <DialogTitle>New Collection</DialogTitle>
                    <DialogDescription>Create a knowledge collection to ground your assistants.</DialogDescription>
                </DialogHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-text-muted)', display: 'block', marginBottom: 6 }}>
                            Collection Name <span style={{ color: 'var(--ds-danger)' }}>*</span>
                        </label>
                        <input
                            className="ds-input"
                            style={{ width: '100%', height: 36 }}
                            placeholder="e.g. Product Docs"
                            value={createForm.name}
                            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            autoFocus
                        />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ds-text-muted)', display: 'block', marginBottom: 6 }}>
                            Vector Database <span style={{ color: 'var(--ds-danger)' }}>*</span>
                        </label>
                        <select
                            className="ds-input"
                            style={{ width: '100%', height: 36 }}
                            value={createForm.vectorStoreType}
                            onChange={(e) => setCreateForm((f) => ({ ...f, vectorStoreType: e.target.value }))}
                        >
                            <option value="MongoDB">MongoDB</option>
                            {hasMilvusCollection && <option value="Milvus">Milvus</option>}
                        </select>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ds-text-subtle)', display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span>Embedding: Jina · Chunking: By Page · Dimensions: 1024</span>
                    </div>
                    {createError && (
                        <div style={{ fontSize: 12, color: 'var(--ds-danger)', background: 'var(--ds-danger-soft)', borderRadius: 6, padding: '8px 12px' }}>
                            {createError}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <DSButton
                        variant="ghost"
                        onClick={() => setShowCreate(false)}
                        disabled={creating}
                        style={{ marginRight: 'auto' }}
                    >
                        Cancel
                    </DSButton>
                    <DSButton
                        variant="primary"
                        onClick={handleCreate}
                        disabled={creating || !createForm.name.trim()}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                        {creating && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                        {creating ? 'Creating…' : 'Create'}
                    </DSButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}
